import { CourseProps, CoursesProps, CreateCourseProps, CreateQuizProps } from "admin/ui";
import { ICourse, IInterview, ILesson, IModule, IPaginateRequest, IQuestion, IQuestionAnswer, IQuiz, IQuizYear, IUserCourse, IUserInterview, IUserQuestionAnswer, IUserQuiz } from "utils/types";
import { useCourse } from "hooks/course";
import { CourseOverviewProps, CourseStudyProps, DashboardProps, ProgressProps } from "dashboard/ui";
import { IAddCourseModuleRequest, ICreateQuestionRequest, IUpdateCourseModuleRequest, 
    IUpdateLessonRequest } from "hooks/course/types";
import { useProfileStore } from "store";
import { useNotification } from "providers";
import { uploadImages } from "utils";

export const useCourseFlow = ():
Pick<CoursesProps, "onGetCourses"|"onGetCoursesStat"> &
Pick<CoursesProps["courseCardProps"], "onDeleteCourse"|"onHideCourse"|"onDeleteQuestions"> &
Pick<CourseProps, "onDeleteCourse"|"onGetQuizYears"> & 
Pick<CreateCourseProps, "onCreateCourse"> &
Pick<CreateQuizProps, "onCreateQuiz"|"onUpdateQuiz"> & 
Pick<CourseOverviewProps, "onGetCourse"|"onGetCourseModules"|"onGetQuizQuestions"> & 
Pick<CourseStudyProps, 
    "onGetUserCourse"|"onCreateUserCourse"|
    "onGetCourseModule"|"onUpdateUserCourse"
> & 
Pick<DashboardProps, "onGetRecommendedCourses"> &
Pick<ProgressProps, "onGetUserQuizzes"|"onGetUserCourses"|"onGetUserQuestionAnswers"|"onGetUserInterviews"> & {
    onGetQuiz: (quizId: string) => Promise<IQuiz|undefined>;
    onDeleteQuiz: (quizId: string, courseId: string) => Promise<IQuizYear[]>;
    onGetCourseById: (courseId: string) => Promise<ICourse|undefined>;
    onGetUserCoursesByUserId: (data: { userId: string; pagination: IPaginateRequest}) => Promise<IUserCourse[]>;
    onUpdateCourseModules: (data: IUpdateCourseModuleRequest[]) => Promise<string[]|undefined>;
    onUpdateCourse: (data: {courseId: string; course: Partial<ICourse>;}) => Promise<ICourse|undefined>;    
    onUpdateLessons: (data: IUpdateLessonRequest[]) => Promise<string[]|undefined>;
    onAddLessons: (data: Partial<ILesson>[]) => Promise<string[]|undefined>;
    onAddCourseModules: (data: IAddCourseModuleRequest[]) => Promise<string[]|undefined>;
    onScoreQuiz: (data: {
        userQuiz: Partial<IUserQuiz>, 
        userQuestionAnswers: Partial<IUserQuestionAnswer>[]
    }) => Promise<IUserQuiz|undefined>;
    onGetCorrectAnswers: (quizId: string) => Promise<IQuestionAnswer[]>;
    onGetCorrectAnswer: (questionId: string) => Promise<IQuestionAnswer|undefined>;
    onGetInterviewCorrectAnswers: (interviewId: string) => Promise<IQuestionAnswer[]>;
    onDeleteQuestionOption: (questionOptionId: string) => Promise<boolean>;
    onGetCourseModuleCount: (courseModule: Partial<IModule>) => Promise<number>;
    onScoreInterview: (data: {
        userInterview: Partial<IUserInterview>, 
        userQuestionAnswers: Partial<IUserQuestionAnswer>[]
    }) => Promise<IUserInterview|undefined>;
    onGetInterviewById: (interviewId: string) => Promise<IInterview|undefined>;
    onDeleteCourseModule: (courseModuleId: string) => Promise<boolean>;
}=> {

    const { user } = useProfileStore();
    const courseHook = useCourse(); 
    const userId = user?.id || ""
    const notification = useNotification();
    return {
        async onDeleteCourseModule(courseModuleId) {
            const res = await courseHook.deleteCourseModule({
                courseModuleId
            })
            if(res){
                notification.success({ message: "course module deleted successfully!"})
            }
            return Boolean(res);
        },
        async onGetCorrectAnswer(questionId) {
            const res = await courseHook.getQuestionAnswers({ 
                questionAnswer: {
                    questionId
                },
                limit: 1,
                skip: 0
            })
            if (res && res.length === 0) {
                return undefined
            }
            return res?.[0]
        },
        async onGetInterviewById(interviewId) {
            return courseHook.getInterview({
                interview: {
                    id: interviewId
                }
            })
        },
        async onGetUserInterviews(data) {
            data.userInterview.userId = userId;
            const res = await courseHook.getUserInterviews(data)
            return res || []
        },
        async onScoreInterview(data) {
            data.userInterview.userId = userId
            const res = await courseHook.scoreInterview(data)
            if(res){
                notification.success({ message: "interview activity updated successfully!"})
            }
            return res;
        },
        async onUpdateQuiz(data) {
            const {quiz, questionAnswers, questionOptions, questions } = data;
            type UpdateInput = Parameters<typeof courseHook.updateQuizUnified>[0]
            const updatedQuestions: UpdateInput["updateQuestionRequest"] = [];
            const newQuestions: UpdateInput["addQuestionRequest"] = [];
            const updatedQuestionOptions: UpdateInput["updateQuestionOptionRequest"] = []
            const addQuestionOptions: UpdateInput["addQuestionOptionRequest"] = [] 
            const updatedQuestionAnswers: UpdateInput["updateQuestionAnswerRequest"] = []
            const addQuestionAnswers: UpdateInput["addQuestionAnswerRequest"] = [] 
            let updatedQuiz: UpdateInput["updateQuizRequest"]|undefined = undefined
            if(!quiz.quizId){
                notification.error({ message: "quizId is required"})
                return undefined;
            }
            if(quiz.quiz){
                updatedQuiz = {
                    quiz: quiz.quiz,
                    quizId: quiz.quizId,
                }
            }
            if(questionAnswers && Object.keys(questionAnswers).length){
                Object.keys(questionAnswers).forEach((questionId) => {
                    Object.keys(questionAnswers[questionId]).forEach((questionAnswerId) => {
                        if(questionAnswerId.includes("-")){
                            updatedQuestionAnswers.push({
                                questionAnswerId,
                                questionAnswer: questionAnswers[questionId][questionAnswerId],
                            })
                        }else {
                            addQuestionAnswers.push({
                                questionAnswer: {
                                    ...questionAnswers[questionId][questionAnswerId],
                                    id: questionAnswerId,
                                }
                            })
                        }
                    })
                })
            }
            if(questions && Object.keys(questions).length){
                Object.keys(questions).forEach((questionId) => {
                    if(questionId.includes("-")){
                        updatedQuestions.push({
                            questionId,
                            question: questions[questionId],
                        })
                    }else {
                        newQuestions.push({
                            question: {
                                ...questions[questionId],
                                id: questionId,
                                quizId: data.quiz.quizId
                            },
                            questionOptions: [],
                        })
                    }
                })
            }
            if(questionOptions && Object.keys(questionOptions).length){
                Object.keys(questionOptions).forEach((questionId) => {
                    Object.keys(questionOptions[questionId]).forEach((questionOptionId) => {
                        const option = questionOptions[questionId][questionOptionId];
                        if(questionOptionId.includes("-")){
                            updatedQuestionOptions.push({
                                questionOptionId,
                                questionOption: option,
                            })
                        }else {
                            if(questionId.includes("-")){
                                addQuestionOptions.push({
                                    questionOption: {
                                        ...option,
                                        questionId
                                    },
                                })
                            }else {
                                const questionIndex = newQuestions.findIndex(q => q.question.id === questionId)
                                if(questionIndex !== -1){
                                    newQuestions[questionIndex].questionOptions.push(option)
                                }
                            }
                        }
                    })
                   
                })
            }
            const input:UpdateInput = {
                updateQuestionRequest: updatedQuestions.length ? updatedQuestions: undefined,
                addQuestionRequest: newQuestions.length ? newQuestions : undefined,
                updateQuestionOptionRequest: updatedQuestionOptions.length ? updatedQuestionOptions : undefined,
                addQuestionOptionRequest: addQuestionOptions.length ? addQuestionOptions : undefined,
                updateQuestionAnswerRequest: updatedQuestionAnswers.length ? updatedQuestionAnswers : undefined,
                addQuestionAnswerRequest: addQuestionAnswers.length ? addQuestionAnswers : undefined,
                quizId: quiz.quizId,
                updateQuizRequest: updatedQuiz,
            }
            const res = await courseHook.updateQuizUnified(input) || undefined
            if(res){
                notification.success({
                    message: "quiz updated successfully!"
                })
            }
            return res;
        },
        async onDeleteQuestionOption(questionOptionId) {
            return Boolean(courseHook.deleteQuestionOption({
                questionOptionId
            }))
        },
        async onGetCourseModuleCount(courseModule) {
            return await courseHook.getCourseModuleCount({
                courseModule
            }) || 0
        },
        async onGetUserQuestionAnswers(quizId) {
            return await courseHook.geUserQuestionAnswers({
                userQuestionAnswer: {
                    quizId
                },
                limit: 150, skip: 0
            }) || []
        },
        async onGetUserCourses(data) {
            if(!data.userCourse){
                data.userCourse = {}
            }
            if(!data.userCourse?.userId){
                data.userCourse.userId = userId
            }
            return await courseHook.getUserCourses(data) || []
        },
        async onGetUserQuizzes(data) {
            if(!data.userQuiz){
                data.userQuiz = {}
            }
            if(!data.userQuiz?.userId){
                data.userQuiz.userId = userId
            }
            return await courseHook.getUserQuizzes(data) || []
        },
        async onGetCorrectAnswers(quizId) {
            return await courseHook.getQuestionAnswers({
                limit: 150, skip: 0,
                questionAnswer: {
                    quizId
                }
            }) || [];
        },
        async onGetInterviewCorrectAnswers(interviewId) {
            return await courseHook.getQuestionAnswers({
                limit: 150, skip: 0,
                questionAnswer: {
                    interviewId
                }
            }) || [];
        },
        async onScoreQuiz(data) {
            data.userQuiz.userId = userId
            return courseHook.scoreQuiz(data)
        },
        async onGetQuizQuestions(quizId) {
            return await courseHook.getQuestions({
                question: {
                    quizId
                },
                limit: 100,
                skip: 0
            }) || []
        },
        async onCreateUserCourse(data) {
            const res = await  courseHook.createUserCourse({
                userCourse: {
                    ...data,
                    userId
                }
            })
            if(res){
                notification.success({ message: "user course activity updated successfully!"})
            }
            return res
        },
        async onGetCourseModule(module) {
            return courseHook.getCourseModule({
                courseModule: module
            })
        },
        async onUpdateUserCourse(userCourseId, data) {
            const res = await courseHook.updateUserCourse({
                userCourseId,
                userCourse: data
            })
            if(res){
                notification.success({ message: "user course activity updated successfully!"})
            }
            return res;
        },
        
        async onAddLessons(data) {            
            const res = await courseHook.addLessons({
                lessons: data
            })
            if(res){
                notification.success({message: "course lesson(s) added successfully!"})
            }
            return res;
        },
        async onAddCourseModules(data) {
            const res = await courseHook.addCourseModules({
                courseModuleAddRequests: data
            })
            if(res){
                notification.success({ 
                    message: "course modules added successfully!"
                })
            }
            return res;
        },
        async onUpdateLessons(data) {
            const res = await courseHook.updateLessons({
                lessonUpdateRequests: data
            })
            if(res){
                notification.success({ message: "course lesson updated successfully!"})
            }
            return res;
        },
        async onGetUserCoursesByUserId(data) {
            return await courseHook.getUserCourses({
                userCourse: {
                    userId: data.userId
                },
                ...data.pagination
            }) || []
        },
        async onGetRecommendedCourses(data) {
            return await courseHook.getRecommendedCourses({
                userId
            }) || []
        },
        async onGetCourseModules(courseId) {
            return await courseHook.getCourseModules({
                courseModule: {
                    courseId
                }, 
                limit: 50,
                skip: 0,
            }) || []
        },
        async onGetUserCourse(courseId) {
            return courseHook.getUserCourse({
                userCourse: {
                    courseId,
                    userId
                }
            })
        },
        async onGetCourse(course) {
            return courseHook.getCourse({
              course
            })
        },
        async onGetQuiz(quizId) {
            return courseHook.getQuiz({
                quiz: {
                    id: quizId
                }
            })      
        },
        async onCreateQuiz(data) {
            const questionRequests:ICreateQuestionRequest[] = [];
            for (let i = 0; i < data.questions.length; i++) {
                const question = data.questions[i];
                const options = question.options;
                if(!options || !question) continue
                question.options = undefined
                questionRequests.push({
                    questionOptions: options,
                    question: question as IQuestion
                })
            }
            return courseHook.createQuiz({
                quiz: data.quiz,
                questionRequests,
                questionAnswers: data.answers
            })
        },
        async onUpdateCourse(data) {
            return await courseHook.updateCourse(data)
        },
        async onUpdateCourseModules(data) {
            return await courseHook.updateCourseModules({
                courseModuleUpdateRequests: data
            });
        },
        async onCreateCourse(data) {            
            // return undefined;
            const moduleRequest: IAddCourseModuleRequest[] = []
            for (let i = 0; i < data.courseModules.length; i++) {
                const cModule = data.courseModules[i];
                if(cModule){
                    cModule.index = i + 1;
                    const lessons = cModule.lessons?.map((item, index) => ({...item, index: index+1}));
                    cModule.lessons = undefined;
                    moduleRequest.push({
                        courseModule: cModule, 
                        lessons: lessons || []
                    })
                }
            }
            const result = await courseHook.createCourse({
                course: data.course,
                courseModulesRequest: moduleRequest
            });
            if(result){
                if(data.imageFile){
                    const images = await uploadImages<{url: string; error: string}>([
                        {
                            file: data.imageFile,
                            name: `course-${Date.now()}`,
                            schemaName: "course",
                            schemaId: result.id
                        }
                    ])
                    if(images.length > 0){
                        notification.success({
                            message: "Course created! - but image not uploaded properly"
                        })
                    }
                    
                }else {
                    notification.success({
                        message: "Course created!"
                    })
                }
            }
            return result;
        },
        async onGetCourseById(courseId) {
            return await courseHook.getCourse({ 
                course: {
                  id: courseId
                }
             });
        },
        async onGetQuizYears(courseId) {
            return await courseHook.getQuizYears({ courseId }) || []
        },
        async onDeleteQuiz(quizId, courseId) {
            return await courseHook.deleteQuizAndGetQuizYears({ quizId, courseId }) || []
        },
        async onGetCourses(data) {
            return await courseHook.getCourses({
                course: data.course,
                search: data.search,
                ...data.pagination
            }) || []
        },
        async onGetCoursesStat() {
            return courseHook.getCourseStat();
        },
        async onDeleteCourse(courseId) {
            const id = await courseHook.deleteCourse({ courseId })
            return Boolean(id);
        },
        async onHideCourse(courseId, shouldHide) {
            const course = await courseHook.updateCourse({
                courseId,
                course: {
                    isHidden: shouldHide
                }
            })
            if(course){
                notification.success({ message: "course visibility changed successfully!"})
            }
            return Boolean(course);
        },
        async onDeleteQuestions(courseId) {
            return true;
        },
    }
}